import React from 'react';
import AdminTenantSelector, { type AdminTenantOption } from '../components/AdminTenantSelector';
import { usePortalContext } from '../context/PortalContext';


type PiiDetectionConfig = {
  enabled: boolean;
};

const defaultConfig: PiiDetectionConfig = {
  enabled: false
};

const PiiDetectionAdmin = (): JSX.Element => {
  const { tenant } = usePortalContext();
  const sessionTenantId = String(tenant?.tenantId ?? '').trim().toLowerCase();
  const hasAccess = tenant?.capabilities?.canReviewAiUsage === true;
  const canEdit = tenant?.capabilities?.canManagePiiPolicy === true;

  const [config, setConfig] = React.useState<PiiDetectionConfig>(defaultConfig);
  const [serviceConfigured, setServiceConfigured] = React.useState(false);
  const [deployableTenants, setDeployableTenants] = React.useState<AdminTenantOption[]>([]);
  const [selectedTenantId, setSelectedTenantId] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [savedMessage, setSavedMessage] = React.useState<string | null>(null);

  const load = React.useCallback(async (): Promise<void> => {
    if (!hasAccess || !selectedTenantId) {
      return;
    }
    setLoading(true);
    setError(null);
    setSavedMessage(null);
    try {
      const response = await fetch(`/api/admin/pii-detection?tenantId=${encodeURIComponent(selectedTenantId)}`);
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload?.message || payload?.error || `Failed to load PII config (${response.status})`);
      }
      setConfig({
        ...defaultConfig,
        ...(payload?.config ?? {})
      });
      setServiceConfigured(Boolean(payload?.serviceConfigured));
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [hasAccess, selectedTenantId]);

  React.useEffect(() => {
    const loadTenants = async (): Promise<void> => {
      if (!hasAccess) {
        return;
      }
      try {
        const response = await fetch('/api/admin/spfx-releases/deployable-tenants');
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(payload?.message || `Failed to load tenants (${response.status})`);
        }
        const tenants = Array.isArray(payload?.tenants) ? payload.tenants as AdminTenantOption[] : [];
        setDeployableTenants(tenants);
        setSelectedTenantId(payload?.defaultTenantId || tenants[0]?.tenantId || sessionTenantId);
      } catch (err) {
        setError((err as Error).message);
      }
    };
    void loadTenants();
  }, [hasAccess, sessionTenantId]);

  React.useEffect(() => {
    void load();
  }, [load]);

  const save = async (): Promise<void> => {
    setSaving(true);
    setError(null);
    setSavedMessage(null);
    try {
      if (!canEdit) {
        throw new Error('You do not have permission to change PII detection settings.');
      }
      const response = await fetch('/api/admin/pii-detection', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tenantId: selectedTenantId, config })
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload?.message || payload?.error || `Failed to save PII config (${response.status})`);
      }
      setServiceConfigured(Boolean(payload?.serviceConfigured));
      setSavedMessage('PII detection settings saved.');
      await load();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  if (!hasAccess) {
    return (
      <div className="card">
        <h1 className="section-title">PII Detection</h1>
        <p className="muted">You do not have permission to manage PII detection settings.</p>
      </div>
    );
  }

  return (
    <div>
      <h1 className="section-title">PII Detection</h1>
      <p className="muted">
        Configure the tenant-controlled personal data removal preview for audit text fields. Users can review detected entities before applying redaction.
      </p>

      <div className="card" style={{ marginTop: '12px' }}>
        <h3>Activation</h3>
        <div style={{ marginTop: '10px', marginBottom: '12px' }}>
          <AdminTenantSelector
            value={selectedTenantId}
            options={deployableTenants}
            onChange={setSelectedTenantId}
            disabled={saving || loading}
            helperText="PII detection policy is tenant-scoped. Configure one covered tenant at a time."
          />
        </div>

        <div style={{ display: 'grid', gap: '12px' }}>
          <label style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <input
              type="checkbox"
              checked={config.enabled}
              onChange={(event) => setConfig((prev) => ({ ...prev, enabled: event.target.checked }))}
            />
            <span>Enable PII detection</span>
          </label>

          <div className="card" style={{ marginTop: '8px' }}>
            <strong>Service status</strong>
            <div className="muted" style={{ marginTop: '6px' }}>
              Azure AI Language configuration: {serviceConfigured ? 'Configured' : 'Unavailable'}
            </div>
            <div className="muted" style={{ marginTop: '6px' }}>
              When enabled, users can remove personal data from supported audit text fields before saving or sending text to AI enhancement.
            </div>
          </div>

          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <button className="btn" type="button" onClick={() => { void save(); }} disabled={saving || loading || !canEdit}>
              {saving ? 'Saving...' : 'Save PII settings'}
            </button>
            <button className="btn secondary" type="button" onClick={() => { void load(); }} disabled={saving || loading}>
              Reload
            </button>
          </div>

          {loading && <span className="badge-subtle">Loading PII settings...</span>}
          {error && <span className="pill warning">{error}</span>}
          {!canEdit && <span className="badge-subtle">Read-only PII settings view</span>}
          {savedMessage && <span className="pill success">{savedMessage}</span>}
        </div>
      </div>
    </div>
  );
};

export default PiiDetectionAdmin;



